<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

import boxLogoIcon from '@clovyr/bed/assets/images/icons/ai-landing/box-logo.svg';
import githubIcon from '@clovyr/bed/assets/images/icons/ai-landing/github.svg';
import globeIcon from '@clovyr/bed/assets/images/icons/ai-landing/globe.svg';
import googleDocsIcon from '@clovyr/bed/assets/images/icons/ai-landing/google-docs.svg';
import googleDriveIcon from '@clovyr/bed/assets/images/icons/ai-landing/google-drive.svg';
import linearIcon from '@clovyr/bed/assets/images/icons/ai-landing/linear.svg';
import slackIcon from '@clovyr/bed/assets/images/icons/ai-landing/slack.svg';
import uploadIcon from '@clovyr/bed/assets/images/icons/ai-landing/upload.svg';
import BaseSvg from '@clovyr/bed/components/icons/BaseSvg.vue';

import jiraLogo from '../../../assets/images/ai-landing/jira-logo.svg';
import notionLogo from '../../../assets/images/ai-landing/notion-logo.svg';
import salesforceLogo from '../../../assets/images/ai-landing/salesforce-logo.svg';

type DocSource = {
  name: string;
  icon: 'google-drive' | 'google-docs' | 'box' | 'upload' | 'globe';
  desc: string;
};

type McpSource = {
  name: string;
  icon: 'notion' | 'salesforce' | 'slack' | 'github' | 'jira' | 'linear';
};

const docSources = [
  { name: 'Google Drive', icon: 'google-drive', desc: 'Docs, Sheets, Slides' },
  { name: 'Google Docs', icon: 'google-docs', desc: 'Live document sync' },
  { name: 'Box', icon: 'box', desc: 'Files & folders' },
  { name: 'Local Upload', icon: 'upload', desc: 'PDF, CSV, Word, code' },
  { name: 'Web Scrape', icon: 'globe', desc: 'Any public URL' },
] satisfies DocSource[];

const mcpSources = [
  { name: 'Notion', icon: 'notion' },
  { name: 'Salesforce', icon: 'salesforce' },
  { name: 'Slack', icon: 'slack' },
  { name: 'GitHub', icon: 'github' },
  { name: 'Jira', icon: 'jira' },
  { name: 'Linear', icon: 'linear' },
] satisfies McpSource[];

const getDocIconSrc = (icon: DocSource['icon']) => {
  if (icon === 'google-drive') {
    return googleDriveIcon;
  }

  if (icon === 'google-docs') {
    return googleDocsIcon;
  }

  if (icon === 'box') {
    return boxLogoIcon;
  }

  if (icon === 'upload') {
    return uploadIcon;
  }

  return globeIcon;
};

const getDocIconViewBox = (icon: DocSource['icon']) => {
  if (icon === 'google-drive') {
    return '0 0 87.3 78';
  }

  if (icon === 'google-docs') {
    return '0 0 64 88';
  }

  if (icon === 'box') {
    return '0 0 120 60';
  }

  return '0 0 24 24';
};

const getMcpIconSrc = (icon: McpSource['icon']) => {
  if (icon === 'slack') {
    return slackIcon;
  }

  if (icon === 'github') {
    return githubIcon;
  }

  return linearIcon;
};

const getMcpIconViewBox = (icon: McpSource['icon']) => {
  if (icon === 'slack' || icon === 'github') {
    return '0 0 100 100';
  }

  return '0 0 24 24';
};

const sectionVisible = ref(false);
const leftPanelVisible = ref(false);
const rightPanelVisible = ref(false);
const footerVisible = ref(false);

const sectionRef = ref<HTMLElement | null>(null);
const leftPanelRef = ref<HTMLElement | null>(null);
const rightPanelRef = ref<HTMLElement | null>(null);
const footerRef = ref<HTMLElement | null>(null);

let observer: IntersectionObserver | undefined;

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        if (entry.target === sectionRef.value) {
          sectionVisible.value = true;
        }

        if (entry.target === leftPanelRef.value) {
          leftPanelVisible.value = true;
        }

        if (entry.target === rightPanelRef.value) {
          rightPanelVisible.value = true;
        }

        if (entry.target === footerRef.value) {
          footerVisible.value = true;
        }

        observer?.unobserve(entry.target);
      });
    },
    {
      threshold: 0.12,
    },
  );

  [sectionRef.value, leftPanelRef.value, rightPanelRef.value, footerRef.value].forEach((node) => {
    if (node) {
      observer?.observe(node);
    }
  });
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <section ref="sectionRef" class="ai-integrations">
    <div class="ai-integrations__inner">
      <header class="ai-integrations__head" :class="{ 'is-visible': sectionVisible }">
        <p>Integrations</p>
        <h2>Your data, <span>wherever it lives.</span></h2>
        <div class="ai-integrations__copy">
          Connect your existing data sources and business tools. Agents read and reason over your
          content without you copying anything anywhere.
        </div>
      </header>

      <div class="ai-integrations__grid">
        <article
          ref="leftPanelRef"
          class="ai-panel"
          :class="{ 'is-visible': leftPanelVisible, 'ai-panel--left': true }"
        >
          <div class="ai-panel__intro">
            <div class="ai-panel__title">Document Sources</div>
            <div class="ai-panel__subtitle">Cloud storage, file platforms, and direct uploads</div>
          </div>

          <ul>
            <li v-for="source in docSources" :key="source.name">
              <div class="ai-panel__icon-slot">
                <BaseSvg
                  class="ai-panel__icon"
                  :viewBox="getDocIconViewBox(source.icon)"
                  aria-hidden="true"
                  role="img"
                  :class="{
                    'ai-panel__icon--drive': source.icon === 'google-drive',
                    'ai-panel__icon--docs': source.icon === 'google-docs',
                    'ai-panel__icon--box': source.icon === 'box',
                    'ai-panel__icon--stroke': source.icon === 'upload' || source.icon === 'globe',
                  }"
                >
                  <image
                    :href="getDocIconSrc(source.icon)"
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </BaseSvg>
              </div>

              <div>
                <strong>{{ source.name }}</strong>
                <small>{{ source.desc }}</small>
              </div>
            </li>
          </ul>
        </article>

        <article
          ref="rightPanelRef"
          class="ai-panel"
          :class="{ 'is-visible': rightPanelVisible, 'ai-panel--right': true }"
        >
          <div class="ai-panel__intro">
            <div class="ai-panel__title">MCP Integrations</div>
            <div class="ai-panel__subtitle">Agents query live data across your stack</div>
          </div>

          <ul class="ai-panel__logos">
            <li v-for="source in mcpSources" :key="source.name">
              <div class="ai-panel__icon-slot">
                <div
                  v-if="source.icon === 'notion'"
                  class="ai-panel__icon-frame ai-panel__icon-frame--notion"
                >
                  <img
                    :src="notionLogo"
                    alt="Notion"
                    class="ai-panel__logo-image ai-panel__logo-image--fill"
                  />
                </div>

                <img
                  v-else-if="source.icon === 'salesforce'"
                  :src="salesforceLogo"
                  alt="Salesforce"
                  class="ai-panel__logo-image ai-panel__logo-image--salesforce"
                />

                <BaseSvg
                  v-else-if="source.icon === 'slack' || source.icon === 'github'"
                  class="ai-panel__icon"
                  :viewBox="getMcpIconViewBox(source.icon)"
                  aria-hidden="true"
                  role="img"
                  :class="{
                    'ai-panel__icon--slack': source.icon === 'slack',
                    'ai-panel__icon--github': source.icon === 'github',
                  }"
                >
                  <image
                    :href="getMcpIconSrc(source.icon)"
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </BaseSvg>

                <img
                  v-else-if="source.icon === 'jira'"
                  :src="jiraLogo"
                  alt="Jira"
                  class="ai-panel__logo-image ai-panel__logo-image--square"
                />

                <BaseSvg
                  v-else-if="source.icon === 'linear'"
                  class="ai-panel__icon ai-panel__icon--linear"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="img"
                >
                  <image
                    :href="linearIcon"
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </BaseSvg>
              </div>

              <div class="ai-panel__logo-label">{{ source.name }}</div>
            </li>
          </ul>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use '../../styles/components/ai-landing-shared' as shared;

.ai-integrations {
  background: #221a33;
}

.ai-integrations__inner {
  max-width: 1360px;
  margin: 0 auto;
  padding: 6rem 2rem;
}

.ai-integrations__head {
  @include shared.section-head(4rem, 20px);
}

.ai-integrations__head.is-visible {
  @include shared.section-head-visible(fadeLift, 0.55s);
}

.ai-integrations__head p {
  @include shared.eyebrow;
}

.ai-integrations__head h2 {
  @include shared.section-title;
}

.ai-integrations__copy {
  @include shared.section-copy(560px);
}

.ai-integrations__head h2 span {
  @include shared.accent-span;
}

.ai-integrations__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}

.ai-panel {
  opacity: 0;
  background: #1e1530;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 1rem;
  padding: 2rem;
  transform: translateX(0);
}

.ai-panel--left {
  transform: translateX(-24px);
}

.ai-panel--right {
  transform: translateX(24px);
}

.ai-panel.is-visible {
  animation: slidePanelIn 0.6s ease forwards;
}

.ai-panel:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

.ai-panel__intro {
  margin-bottom: 1.5rem;
}

.ai-panel__title {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.ai-panel__subtitle {
  color: #4b5563;
  font-size: 0.875rem;
}

.ai-panel ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.625rem;
}

.ai-panel ul li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #2a1f40;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  padding: 0.875rem 1.125rem;
  transition:
    border-color 300ms ease,
    background-color 300ms ease;
}

.ai-panel ul li strong {
  display: block;
  font-size: 0.875rem;
  color: #fff;
  line-height: 1.25;
  font-weight: 500;
}

.ai-panel ul li small {
  display: block;
  margin-top: 0.15rem;
  color: #4b5563;
  font-size: 0.75rem;
}

.ai-panel ul li:hover {
  border-color: rgba(0, 191, 116, 0.3);
  background: rgba(0, 191, 116, 0.05);
}

.ai-panel__icon-slot {
  display: flex;
  width: 1.5rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}

.ai-panel__icon {
  display: block;
}

.ai-panel__icon--drive {
  width: 1.25rem;
  height: 1.25rem;
}

.ai-panel__icon--docs {
  width: 1rem;
  height: 1.25rem;
}

.ai-panel__icon--box {
  width: 2.375rem;
  height: 1.35rem;
}

.ai-panel__icon--stroke {
  width: 1.125rem;
  height: 1.125rem;
  color: #9ca3af;
}

.ai-panel__logos {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.875rem;
}

.ai-panel__logos li {
  min-height: auto;
}

.ai-panel__icon-frame {
  display: flex;
  width: 1.25rem;
  height: 1.25rem;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 0.125rem;
}

.ai-panel__icon-frame--notion {
  background: #fff;
}

.ai-panel__logo-image {
  display: block;
  object-fit: contain;
}

.ai-panel__logo-image--fill {
  width: 100%;
  height: 100%;
}

.ai-panel__logo-image--salesforce {
  width: 4rem;
  height: auto;
}

.ai-panel__logo-image--square {
  width: 1.25rem;
  height: 1.25rem;
}

.ai-panel__logo-image--linear {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0;
}

.ai-panel__icon--linear {
  width: 1.5rem;
  height: 1.5rem;
  color: #fff;
}

.ai-panel__icon--slack,
.ai-panel__icon--github {
  width: 1.25rem;
  height: 1.25rem;
}

.ai-panel__logo-label {
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
}

.ai-integrations__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.5rem;
  opacity: 0;
}

.ai-integrations__footer.is-visible {
  animation: fadeOnly 0.45s ease forwards;
}

.ai-integrations__footer-text {
  color: #4b5563;
  font-size: 0.875rem;
}

.ai-integrations__footer-icon {
  width: 0.875rem;
  height: 0.875rem;
  color: #00bf74;
}

@media (min-width: 980px) {
  .ai-integrations__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1199px) {
  .ai-integrations__inner {
    padding: 5.75rem 1.75rem;
  }

  .ai-panel {
    padding: 1.5rem;
  }
}

@media (max-width: 979px) {
  .ai-integrations__head {
    margin-bottom: 2.75rem;
  }

  .ai-panel {
    transform: none;
  }

  .ai-panel ul li {
    padding: 0.8rem 1rem;
  }
}

@media (max-width: 800px) {
  .ai-integrations__inner {
    padding: 5.5rem 1.5rem;
  }

  .ai-panel__logos {
    gap: 0.7rem;
  }
}

@media (max-width: 640px) {
  .ai-integrations__inner {
    padding: 5rem 1rem;
  }

  .ai-panel {
    padding: 1.1rem;
  }

  .ai-panel ul li {
    gap: 0.6rem;
    padding: 0.7rem 0.8rem;
  }

  .ai-panel__logos {
    grid-template-columns: 1fr;
  }

  .ai-panel ul li strong,
  .ai-panel__logo-label {
    font-size: 0.83rem;
  }
}

@include shared.keyframes-fade-lift(fadeLift, 20px);

@keyframes slidePanelIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@include shared.keyframes-fade-only(fadeOnly);
</style>
